import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, Card, DatePicker, Space, Table, Statistic, Row, Col, Alert, Spin, theme, Divider, Select } from 'antd'
import { FileTextOutlined, CalendarOutlined, RiseOutlined, FallOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { apiService, User } from '@/lib/api'
import DashboardLayout from '@/components/DashboardLayout'
import dayjs, { Dayjs } from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'

dayjs.extend(quarterOfYear)

const { Title, Text } = Typography
const { RangePicker } = DatePicker

interface Account { id: number; account_code: string; account_name: string; total: number }
interface ProfitAndLossData {
  period: { start_date: string; end_date: string }
  revenue: { accounts: Account[]; total: number }
  expenses: { accounts: Account[]; total: number }
  net_income: number
}

type QuickPeriod = 'custom' | 'this-month' | 'last-month' | 'this-quarter' | 'this-year' | 'ytd'

export default function ProfitAndLossPage() {
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const [user, setUser] = useState<User | null>(null)
  const [startDate, setStartDate] = useState<string>(dayjs().startOf('month').format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'))
  const [quickPeriod, setQuickPeriod] = useState<QuickPeriod>('this-month')

  useEffect(() => { const storedUser = localStorage.getItem('user'); if (storedUser) setUser(JSON.parse(storedUser)) }, [])

  const handleLogout = () => { localStorage.removeItem('authToken'); localStorage.removeItem('user'); navigate('/') }

  const { data: profitAndLossResponse, isLoading, error } = useQuery({
    queryKey: ['profit-and-loss', startDate, endDate],
    queryFn: async () => {
      const response = await apiService.getProfitAndLoss(startDate, endDate)
      if (response.success && response.data) return response
      throw new Error(response.message || 'Failed to load profit and loss')
    }
  })

  const profitAndLoss = profitAndLossResponse?.data as ProfitAndLossData | undefined

  const handleQuickPeriodChange = (period: QuickPeriod) => {
    setQuickPeriod(period)
    const today = dayjs()
    switch (period) {
      case 'this-month': setStartDate(today.startOf('month').format('YYYY-MM-DD')); setEndDate(today.format('YYYY-MM-DD')); break
      case 'last-month': setStartDate(today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD')); setEndDate(today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD')); break
      case 'this-quarter': setStartDate(today.startOf('quarter').format('YYYY-MM-DD')); setEndDate(today.format('YYYY-MM-DD')); break
      case 'this-year': setStartDate(today.startOf('year').format('YYYY-MM-DD')); setEndDate(today.endOf('year').format('YYYY-MM-DD')); break
      case 'ytd': setStartDate(today.startOf('year').format('YYYY-MM-DD')); setEndDate(today.format('YYYY-MM-DD')); break
    }
  }

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) { setStartDate(dates[0].format('YYYY-MM-DD')); setEndDate(dates[1].format('YYYY-MM-DD')); setQuickPeriod('custom') }
  }

  const accountColumns = [
    { title: 'Code', dataIndex: 'account_code', key: 'account_code', width: 120, render: (text: string) => <Text strong>{text}</Text> },
    { title: 'Account Name', dataIndex: 'account_name', key: 'account_name' },
    { title: 'Total', dataIndex: 'total', key: 'total', align: 'right' as const, width: 180, render: (total: number) => <Text strong style={{ color: total < 0 ? token.colorError : token.colorText }}>MYR {Number(total).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text> }
  ]

  const isProfit = profitAndLoss ? profitAndLoss.net_income >= 0 : true

  return (
    <DashboardLayout user={user} onLogout={handleLogout} title="Profit & Loss Statement" subtitle="View your company's income and expenses" titleIcon={<FileTextOutlined />}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col><Text strong>Period:</Text></Col>
            <Col><Select value={quickPeriod} onChange={handleQuickPeriodChange} style={{ width: 150 }} options={[{ value: 'this-month', label: 'This Month' }, { value: 'last-month', label: 'Last Month' }, { value: 'this-quarter', label: 'This Quarter' }, { value: 'ytd', label: 'Year to Date' }, { value: 'this-year', label: 'This Year' }, { value: 'custom', label: 'Custom' }]} /></Col>
            <Col><RangePicker value={[dayjs(startDate), dayjs(endDate)]} onChange={handleDateRangeChange} format="DD MMM YYYY" suffixIcon={<CalendarOutlined />} /></Col>
          </Row>
        </Card>

        {isLoading && <Card><div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /><div style={{ marginTop: 16 }}><Text type="secondary">Loading profit and loss statement...</Text></div></div></Card>}
        {error && <Alert message="Error Loading Profit & Loss" description={error instanceof Error ? error.message : 'An error occurred'} type="error" showIcon />}

        {profitAndLoss && !isLoading && (
          <>
            <Card>
              <Row gutter={16} align="middle">
                <Col flex={1}><Statistic title={isProfit ? 'Net Income (Profit)' : 'Net Loss'} value={Math.abs(profitAndLoss.net_income)} precision={2} prefix="MYR" valueStyle={{ color: isProfit ? token.colorSuccess : token.colorError }} suffix={isProfit ? <RiseOutlined /> : <FallOutlined />} /></Col>
                <Col><Space direction="vertical" size="small" style={{ textAlign: 'right' }}><Text type="secondary">Period: {dayjs(profitAndLoss.period.start_date).format('DD MMM YYYY')} - {dayjs(profitAndLoss.period.end_date).format('DD MMM YYYY')}</Text><Alert message={isProfit ? 'Profitable Period' : 'Loss Period'} type={isProfit ? 'success' : 'warning'} showIcon style={{ padding: '4px 12px' }} /></Space></Col>
              </Row>
            </Card>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}><Card><Statistic title="Total Revenue" value={profitAndLoss.revenue.total} precision={2} prefix="MYR" valueStyle={{ color: token.colorSuccess }} /></Card></Col>
              <Col xs={24} sm={12}><Card><Statistic title="Total Expenses" value={profitAndLoss.expenses.total} precision={2} prefix="MYR" valueStyle={{ color: token.colorError }} /></Card></Col>
            </Row>

            <Card title={<Text strong style={{ fontSize: 16, color: token.colorSuccess }}>REVENUE</Text>}>
              {profitAndLoss.revenue.accounts.length > 0 ? (
                <Table columns={accountColumns} dataSource={profitAndLoss.revenue.accounts} rowKey="id" pagination={false} size="small" scroll={{ x: 500 }} summary={() => (
                  <Table.Summary><Table.Summary.Row style={{ backgroundColor: token.colorSuccessBg }}><Table.Summary.Cell index={0} colSpan={2}><Text strong style={{ fontSize: 16 }}>Total Revenue</Text></Table.Summary.Cell><Table.Summary.Cell index={2} align="right"><Text strong style={{ fontSize: 16, color: token.colorSuccess }}>MYR {Number(profitAndLoss.revenue.total).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Table.Summary.Cell></Table.Summary.Row></Table.Summary>
                )} />
              ) : <Alert message="No revenue recorded for this period" type="info" showIcon />}
            </Card>

            <Card title={<Text strong style={{ fontSize: 16, color: token.colorError }}>EXPENSES</Text>}>
              {profitAndLoss.expenses.accounts.length > 0 ? (
                <Table columns={accountColumns} dataSource={profitAndLoss.expenses.accounts} rowKey="id" pagination={false} size="small" summary={() => (
                  <Table.Summary><Table.Summary.Row style={{ backgroundColor: token.colorErrorBg }}><Table.Summary.Cell index={0} colSpan={2}><Text strong style={{ fontSize: 16 }}>Total Expenses</Text></Table.Summary.Cell><Table.Summary.Cell index={2} align="right"><Text strong style={{ fontSize: 16, color: token.colorError }}>MYR {Number(profitAndLoss.expenses.total).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Table.Summary.Cell></Table.Summary.Row></Table.Summary>
                )} />
              ) : <Alert message="No expenses recorded for this period" type="info" showIcon />}
            </Card>

            <Card>
              <div style={{ padding: 16 }}>
                <Row justify="space-between" style={{ padding: '12px 0', fontSize: 16 }}><Col><Text strong>Total Revenue</Text></Col><Col><Text strong style={{ color: token.colorSuccess }}>MYR {Number(profitAndLoss.revenue.total).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Col></Row>
                <Row justify="space-between" style={{ padding: '12px 0', fontSize: 16 }}><Col><Text strong>Total Expenses</Text></Col><Col><Text strong style={{ color: token.colorError }}>MYR {Number(profitAndLoss.expenses.total).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Col></Row>
                <Divider style={{ margin: '16px 0' }} />
                <div style={{ backgroundColor: isProfit ? token.colorSuccessBg : token.colorErrorBg, padding: 16, borderRadius: 8, border: `2px solid ${isProfit ? token.colorSuccessBorder : token.colorErrorBorder}` }}>
                  <Row justify="space-between"><Col><Text strong style={{ fontSize: 18 }}>{isProfit ? 'Net Income (Profit)' : 'Net Loss'}</Text></Col><Col><Text strong style={{ fontSize: 20, color: isProfit ? token.colorSuccess : token.colorError }}>{isProfit ? <RiseOutlined /> : <FallOutlined />} MYR {Number(Math.abs(profitAndLoss.net_income)).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Col></Row>
                </div>
              </div>
            </Card>
          </>
        )}
      </Space>
    </DashboardLayout>
  )
}
